import dayjs from "dayjs";
import * as yup from "yup";
import { AddressData, PersonaStructure } from "@interfaces/response";
import { PayloadPersonPatch } from "@interfaces/request";
import { PersonFormFieldCode, PersonFormFieldConfig } from "@components/PersonForm/personFormConstants";
import { CURP, EMAIL, RFC_PFAE } from "@utils/expreciones";
import { MENSAJE_EDAD_MAXIMA } from "@helpers/constantes";
import { esFechaNacimientoDentroDeEdadMaxima } from "@helpers/funciones";

/** Id ficticio de la opción "Otro" en el select de cónyuge; indica que hay que crear a la persona. */
export const OTHER_PERSON_ID = "1-2-3-4-5";

/** Valor de `colonia_id` cuando el usuario captura una colonia que no viene en el catálogo del CP. */
export const OTHER_COLONIA_ID = "-1";

export type PersonFormFields = Record<PersonFormFieldCode, PersonFormFieldConfig>;

export type AddressFormValues = {
  cp: string;
  street: string;
  no_ext: string;
  no_int: string;
  state_id: string;
  city_id: string;
  locality_id: string;
  colonia_id: string;
  state: string;
  city: string;
  locality: string;
  other_neighborhood: string;
};

export type PersonFormValues = {
  country_code: string;
  phone_number: string;
  email: string;
  occupation: string;
  rfc: string;
  curp: string;
  civil_status_id: string;
  matrimonial_regimes_id: string;
  spouse_id: string;
  spouse_name: string;
  spouse_last_name: string;
  spouse_last_name_2: string;
  birth_date: string;
  birth_state: string;
  sex: string;
  particular: AddressFormValues;
  operativo: AddressFormValues;
};

export const emptyAddress = (): AddressFormValues => ({
  cp: "",
  street: "",
  no_ext: "",
  no_int: "",
  state_id: "",
  city_id: "",
  locality_id: "",
  colonia_id: "",
  state: "",
  city: "",
  locality: "",
  other_neighborhood: "",
});

/** Convierte un domicilio del API a valores del formulario; sin domicilio regresa uno vacío. */
export const hydrateAddress = (address?: AddressData): AddressFormValues => {
  if (!address) return emptyAddress();
  return {
    cp: address.cp || "",
    street: address.street || "",
    no_ext: address.no_ext || "",
    no_int: address.no_int || "",
    state_id: address.state_id ? String(address.state_id) : "",
    city_id: address.city_id ? String(address.city_id) : "",
    locality_id: address.locality?.id ? String(address.locality.id) : "",
    colonia_id: address.colonia_id ? String(address.colonia_id) : address.other_neighborhood ? OTHER_COLONIA_ID : "",
    state: address.state?.name || "",
    city: address.city?.name || "",
    locality: address.locality?.name || "",
    other_neighborhood: address.other_neighborhood || "",
  };
};

/** Valores iniciales del formulario a partir de la persona y sus domicilios guardados; sin persona, todo vacío. */
export const buildInitialValues = (
  person?: PersonaStructure,
  particular?: AddressData,
  operativo?: AddressData,
): PersonFormValues => ({
  country_code: person?.code_phone_number || "+52",
  phone_number: person?.phone_number || "",
  email: person?.email || "",
  occupation: person?.occupation || "",
  rfc: person?.rfc || "",
  curp: person?.curp || "",
  civil_status_id: person?.civil_status_id || "",
  matrimonial_regimes_id: person?.matrimonial_regimes_id || "",
  spouse_id: person?.spouse_id || "",
  spouse_name: "",
  spouse_last_name: "",
  spouse_last_name_2: "",
  birth_date: person?.birth_date || "",
  birth_state: person?.birth_state ? String(person.birth_state) : "",
  sex: person?.sex || "",
  particular: hydrateAddress(particular),
  operativo: hydrateAddress(operativo),
});

const addressYup = (required: boolean) =>
  yup.object({
    cp: required
      ? yup
          .string()
          .required("Requerido")
          .matches(/^[0-9]{5}$/, "Código postal inválido")
      : yup.string(),
    street: required ? yup.string().required("Requerido") : yup.string(),
    no_ext: required ? yup.string().required("Requerido") : yup.string(),
    no_int: yup.string(),
    state_id: required ? yup.string().required("Requerido") : yup.string(),
    city_id: yup.string(),
    locality_id: yup.string(),
    colonia_id: required ? yup.string().required("Requerido") : yup.string(),
    other_neighborhood: yup.string().when("colonia_id", {
      is: OTHER_COLONIA_ID,
      then: (schema) => (required ? schema.required("Campo requerido") : schema),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

/**
 * Campo de texto: sin validar si está deshabilitado; si está habilitado, aplica `withRules`
 * y además lo vuelve obligatorio cuando `required` es true.
 */
const textField = (
  enabled: boolean,
  required: boolean,
  withRules: (schema: yup.StringSchema) => yup.StringSchema = (schema) => schema,
) => {
  if (!enabled) return yup.string();
  return withRules(required ? yup.string().required("Requerido") : yup.string());
};

/** Campo habilitado que solo es obligatorio si `required` es true y el estado civil es casado. */
const whenMarried = (enabled: boolean, required: boolean, marriedId: string) =>
  enabled
    ? yup.string().when("civil_status_id", {
        is: marriedId,
        then: (schema) => (required ? schema.required("Requerido") : schema),
        otherwise: (schema) => schema.notRequired(),
      })
    : yup.string();

/** Campo obligatorio solo cuando se elige "Otro" como cónyuge. */
const whenOtherSpouse = () =>
  yup.string().when("spouse_id", {
    is: OTHER_PERSON_ID,
    then: (schema) => schema.required("Requerido"),
    otherwise: (schema) => schema.notRequired(),
  });

const isAdult = (schema: yup.StringSchema) =>
  schema
    .test("edad-maxima", MENSAJE_EDAD_MAXIMA, esFechaNacimientoDentroDeEdadMaxima)
    .test("isAdult", "Debes ser mayor de 18 años", (value) => {
      if (!value) return false;
      return dayjs().diff(dayjs(value), "years") >= 18;
    });

/** Schema de Yup según los campos habilitados/obligatorios de la persona. */
export const buildValidationSchema = (fields: PersonFormFields, marriedId: string) => {
  const en = (code: PersonFormFieldCode) => Boolean(fields[code]?.enabled);
  const req = (code: PersonFormFieldCode) => en(code) && Boolean(fields[code]?.required);

  return yup.object({
    country_code: en("cell_phone_number") ? yup.string().required("Requerido") : yup.string(),
    phone_number: textField(en("cell_phone_number"), req("cell_phone_number"), (schema) =>
      schema.matches(/^[0-9]{10}$/, "Ingresa 10 dígitos"),
    ),
    email: textField(en("email"), req("email"), (schema) => schema.matches(EMAIL, "Correo inválido")),
    occupation: textField(en("occupation"), req("occupation")),
    rfc: textField(en("rfc"), req("rfc"), (schema) => schema.matches(RFC_PFAE, "Formato de RFC no válido")),
    curp: textField(en("curp"), req("curp"), (schema) => schema.matches(CURP, "Formato de CURP no válido")),
    civil_status_id: textField(en("civil_status_id"), req("civil_status_id")),
    matrimonial_regimes_id: whenMarried(en("matrimonial_regimes_id"), req("matrimonial_regimes_id"), marriedId),
    spouse_id: whenMarried(en("spouse_id"), req("spouse_id"), marriedId),
    spouse_name: whenOtherSpouse(),
    spouse_last_name: whenOtherSpouse(),
    spouse_last_name_2: yup.string(),
    // la validación de edad solo aplica cuando la fecha es obligatoria
    birth_date: req("birth_date") ? isAdult(yup.string().required("Requerido")) : yup.string(),
    birth_state: textField(en("birth_state"), req("birth_state")),
    sex: textField(en("sex"), req("sex")),
    particular: addressYup(req("domicilio_particular")),
    operativo: addressYup(req("domicilio_operativo")),
  });
};

/**
 * Payload del PATCH de persona: siempre marca la información como completa y solo agrega
 * los campos habilitados. Si la persona no es casada, régimen y cónyuge se mandan en null.
 */
export const buildPersonPatchPayload = ({
  values,
  fields,
  isMarried,
  spouseId,
}: {
  values: PersonFormValues;
  fields: PersonFormFields;
  isMarried: boolean;
  spouseId: string | null;
}): PayloadPersonPatch => {
  const payload: PayloadPersonPatch = {
    information_person_completed: true,
    personal_information_status: "COMPLETO",
  };

  if (fields.cell_phone_number?.enabled) {
    payload.code_phone_number = values.country_code;
    payload.phone_number = values.phone_number;
  }
  if (fields.email?.enabled) payload.email = values.email;
  if (fields.occupation?.enabled) payload.occupation = values.occupation;
  if (fields.rfc?.enabled) payload.rfc = values.rfc;
  if (fields.curp?.enabled) payload.curp = values.curp;
  if (fields.civil_status_id?.enabled) payload.civil_status_id = values.civil_status_id;
  if (fields.matrimonial_regimes_id?.enabled) {
    payload.matrimonial_regimes_id = isMarried ? values.matrimonial_regimes_id || null : null;
  }
  if (fields.spouse_id?.enabled) {
    payload.spouse_id = isMarried ? spouseId : null;
  }
  if (fields.birth_date?.enabled) payload.birth_date = values.birth_date;
  if (fields.birth_state?.enabled) payload.birth_state = values.birth_state ? +values.birth_state : undefined;
  if (fields.sex?.enabled) payload.sex = values.sex;

  return payload;
};

/**
 * Payload del POST/PATCH de domicilio (particular u operativo), siempre ligado a la persona.
 * Con "Otra" colonia, `colonia_id` va en null.
 */
export const buildAddressPayload = ({
  values,
  clientDetailsId,
  addressTypeId,
  personId,
}: {
  values: AddressFormValues;
  clientDetailsId: string;
  addressTypeId: string;
  personId: string;
}) => ({
  client_details_id: clientDetailsId,
  person_id: personId,
  address_type_id: addressTypeId,
  cp: values.cp,
  street: values.street,
  no_ext: values.no_ext,
  no_int: values.no_int,
  country_id: 1,
  state_id: values.state_id ? +values.state_id : null,
  city_id: values.city_id ? +values.city_id : null,
  locality_id: values.locality_id ? +values.locality_id : null,
  colonia_id: values.colonia_id === OTHER_COLONIA_ID ? null : Number(values.colonia_id),
  other_neighborhood: values.other_neighborhood,
});
